<template>

  <div class="projects-view">

    <div v-if="loading" class="projects-view-loading">

      <b-spinner small variant="primary" class="mr-50" />

      <span>Loading projects…</span>

    </div>



    <template v-else>

      <div v-if="!visibleProjects.length" class="projects-view-empty">

        <feather-icon icon="FolderPlusIcon" size="40" class="projects-view-empty-icon" />

        <h5 class="font-weight-bold mb-50">No projects yet</h5>

        <p class="text-muted small mb-1">

          Group tasks into projects and assign team roles.

        </p>

        <b-button v-if="canCreate" size="sm" variant="primary" @click="$emit('create-project')">

          Create Project

        </b-button>

      </div>



      <div v-else-if="selectedProject" class="projects-view-detail-wrap">

        <div class="projects-view-detail-page">

          <div class="project-detail-hero">

            <div class="project-detail-hero-main">

              <div class="d-flex align-items-start project-detail-title-row">

                <span

                  class="project-color-dot project-color-dot--lg flex-shrink-0"

                  :style="{ backgroundColor: selectedProject.color || '#7367F0' }"

                />

                <div class="min-width-0 flex-grow-1">

                  <div class="d-flex flex-wrap align-items-center project-detail-name-row">

                    <h5 class="mb-0 font-weight-bold text-truncate">{{ selectedProject.name }}</h5>

                    <b-badge :variant="`light-${statusVariant(selectedProject.status)}`" class="project-status-badge">

                      {{ selectedProject.status || 'Active' }}

                    </b-badge>

                  </div>

                  <p v-if="selectedProject.description" class="text-muted small mb-0 mt-50 project-detail-desc">

                    {{ selectedProject.description }}

                  </p>

                  <p v-else class="text-muted small mb-0 mt-50 project-detail-desc project-detail-desc--empty">

                    No description provided.

                  </p>

                  <div class="project-detail-stat-pills">

                    <span class="project-stat-pill">

                      <feather-icon icon="CheckSquareIcon" size="12" />

                      {{ taskCountFor(projectId(selectedProject)) }} {{ taskCountFor(projectId(selectedProject)) === 1 ? 'task' : 'tasks' }}

                    </span>

                    <span class="project-stat-pill-sep" aria-hidden="true">·</span>

                    <span class="project-stat-pill">

                      <feather-icon icon="UsersIcon" size="12" />

                      {{ memberCountFor(selectedProject) }} {{ memberCountFor(selectedProject) === 1 ? 'member' : 'members' }}

                    </span>

                  </div>

                </div>

              </div>

            </div>

            <b-button

              size="sm"

              variant="outline-primary"

              class="flex-shrink-0 project-detail-edit-btn"

              @click="$emit('edit-project', selectedProject)"

            >

              <feather-icon icon="EditIcon" size="13" class="mr-25" />Project settings

            </b-button>

          </div>



          <div class="project-team-section">

            <project-team-panel

              :project="selectedProject"

              :current-user="currentUser"

              :assignable-users="assignableUsers"

              @add-member="$emit('add-member', $event)"

              @update-member="$emit('update-member', $event)"

              @remove-member="$emit('remove-member', $event)"

              @assign-responsibility="$emit('assign-responsibility', $event)"

              @add-responsibility="$emit('add-responsibility', $event)"

            />

          </div>

        </div>

      </div>



      <div v-else class="projects-view-select-hint">

        <feather-icon icon="MousePointerIcon" size="32" class="text-muted mb-75" />

        <h5 class="font-weight-bold mb-50">Select a project</h5>

        <p class="text-muted small mb-1">

          Choose a project from the sidebar to view team details and settings.

        </p>

        <b-button v-if="canCreate" size="sm" variant="primary" @click="$emit('create-project')">

          <feather-icon icon="PlusIcon" size="14" class="mr-50" />New Project

        </b-button>

      </div>

    </template>

  </div>

</template>



<script>

import {

  BBadge, BButton, BSpinner,

} from 'bootstrap-vue'

import ProjectTeamPanel from './ProjectTeamPanel.vue'

import {

  normalizeProjectStatus,

  projectStatusVariant,

  projectMemberCount,

  projectId,

} from '@/utils/project-config'

import {

  visibleProjects as filterVisibleProjects,

  canCreateProject,

} from '@/utils/project-permissions'



export default {

  name: 'ProjectsView',

  components: {

    BBadge, BButton, BSpinner,

    ProjectTeamPanel,

  },

  props: {

    projects: { type: Array, default: () => [] },

    tasks: { type: Array, default: () => [] },

    /** Scope-filtered tasks for count badges — see TaskBoard `sidebarCountTasks`. */
    countTasks: { type: Array, default: () => [] },

    selectedProjectId: { type: [String, Number], default: null },

    currentUser: { type: Object, default: () => ({}) },

    assignableUsers: { type: Array, default: () => [] },

    loading: { type: Boolean, default: false },

  },

  computed: {

    visibleProjects() {

      return filterVisibleProjects(this.projects, this.currentUser, this.tasks)

    },

    selectedProject() {

      if (this.selectedProjectId == null) return null

      const id = String(this.selectedProjectId)

      return this.projects.find(p => String(projectId(p)) === id)

        || this.visibleProjects.find(p => String(projectId(p)) === id)

        || null

    },

    canCreate() {

      return canCreateProject()

    },

  },

  methods: {

    projectId(project) {

      return projectId(project)

    },

    statusVariant(status) {

      return projectStatusVariant(normalizeProjectStatus({ status }))

    },

    taskCountFor(projectId) {

      return (this.countTasks || []).filter(t => {

        const pid = t.projectId ?? t.project_id

        return pid != null && String(pid) === String(projectId)

      }).length

    },

    memberCountFor(project) {

      return projectMemberCount(project)

    },

  },

}

</script>



<style lang="scss" scoped>

.min-width-0 {

  min-width: 0;

}

</style>

